import { h } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import Panel from 'utils/panel';
import TrapFocus from 'utils/trap-focus';
import { formatCartLinesAjax } from 'utils/storefront/format-resource';
import { CloseV2 } from 'utils/icon/icons';
import { trackMiniCartATB } from 'utils/tracking';
import { isCampaignSatisfy, autoDeleteGwpFromCart } from 'utils/gwp';
import FormatMoney from '../utils/format-money';
import { withCartContext } from './cart-context';
import CartItem from './cart-item';
import Upsells from './upsells';
import FreeShipping from './cart-shipping';
import updateURL from '../utils/update-url';
import Layout from '../utils/layout';
import GwpMultipleProductItem from './gwp-multiple-product-item';
import GwpAutoAddBanner from './gwp-auto-add-banner';
import { getGwpDismissedIds, setGwpDismissedIds } from './gwp-session-storage';

// Utility to decode HTML entities.
// Shopify/Liquid or the translation pipeline may HTML-escape special characters (like & to &amp;),
// so we decode them here to ensure the correct display in the UI.
function decodeHTMLEntities(text) {
  if (!text) return '';
  const txt = document.createElement('textarea');
  txt.innerHTML = text;
  return txt.value;
}

const Cart = ({
  cart,
  maxOrderQTY,
  upSellProducts,
  shUpTitle,
  freeShippingThreshold,
  freeShippingIconThresholdMet,
  freeShippingIconThresholdUnfulfilled,
  freeShippingMessageThresholdMet,
  freeShippingMessageThresholdUnfulfilled,
  gwpAutoAddData,
  gwpMultipleId,
  gwpMultipleIdx,
  gwpMultipleCampaignType,
  gwpMultiplePurchaseThreshold,
  gwpMultipleQuantityThreshold,
  gwpMultipleGiftLimit,
  gwpMultipleTagFilterType,
  gwpMultipleAllowedProductTags,
  gwpMultipleExcludedProductTags,
  gwpMultipleBannerTitle,
  gwpMultipleProductTitle,
  gwpMultiplePromoMessage,
  gwpMultipleProductMessage,
  gwpMultipleCampaignImage,
  gwpMultipleProductList,
  gwpMultipleProductTitle1,
  gwpMultipleProductTitle2,
  gwpMultipleProductTitle3,
  gwpMultipleProductSubtitle,
  gwpMultipleProductBadge,
  promosWithThresholds = [],
  customerMarket,
  timerId,
  timerEnabled,
  promoBadges,
  savingsMessage,
  emptyCartButtons = [],
}) => {
  const lines = formatCartLinesAjax(cart?.state?.items);

  let totalComparePrice = 0; // the total from all Compare-At Prices (i.e. the Compare-At Price field in the product admin)
  let totalPriceWithComparePrice = 0; // (AKA the sale price from the product admin) calc only when Compare-At Price > 0
  let totalLineLevelDiscount = 0; // includes Discount Codes (e.g. "SUMMER21") and Automatic Discounts (e.g. "Spend $50 and get 10% off")

  lines.forEach((item) => {
    // client doesn't want Compare-At Price included in the Totals Savings if a Discount Code is used
    if (item.properties?._compare_price && parseFloat(item.line_level_total_discount) === 0) {
      totalComparePrice += parseFloat(item.properties._compare_price) * item.quantity;
      totalPriceWithComparePrice += (parseFloat(item.final_price) * item.quantity) / 100;
    }

    totalLineLevelDiscount += parseFloat(item.line_level_total_discount) / 100;
  });

  // get difference from all Compare-At Prices and regular non-discounted prices
  const totalLinesWithComparePrice = totalComparePrice - totalPriceWithComparePrice;
  // get sum of all line-level discounts, so result is something like $24 in total savings
  const totalSavings = totalLinesWithComparePrice + totalLineLevelDiscount;

  const closeTriggerEl = useRef(null);
  const isCartRedirect = new URLSearchParams(window.location.search).get('cart');
  const [panelTriggers, setPanelTriggers] = useState({
    openTriggerEl: document.querySelector('[data-header-cart-trigger]'),
    closeTriggerEl: null,
  });
  const [isOpen, setIsOpen] = useState(isCartRedirect);
  const layout = Layout.getInstance();
  const [isMobile, setIsMobile] = useState(); // based on screen width

  const [gwpDeletedFromCartCount, setGwpDeletedFromCartCount] = useState(0);
  const [gwpAutoAddDeletedIds, setGwpAutoAddDeletedIds] = useState(getGwpDismissedIds());
  const gwpMultipleProductListAvailable = gwpMultipleProductList.filter((p) => p?.available);
  const isGwpMultipleReady =
    (gwpMultipleProductListAvailable.length > 0 &&
      gwpMultipleProductListAvailable.some((p) => p?.available) &&
      ((gwpMultipleCampaignType === 'purchase' && Number.parseFloat(gwpMultiplePurchaseThreshold) > 0) ||
        (gwpMultipleCampaignType === 'quantity' && Number.parseInt(gwpMultipleQuantityThreshold, 10) > 0))) ||
    false;
  const gwpMultipleLineItems = useMemo(
    () => (isGwpMultipleReady ? lines?.filter((item) => !!item?.properties?._multiple_gwp) : []),
    [cart.state.items],
  );
  const totalQuantityOfGiftProductsInCart = useMemo(
    () => (isGwpMultipleReady ? gwpMultipleLineItems?.reduce((total, item) => total + item.quantity, 0) || 0 : 0),
    [cart.state],
  );
  const isGwpMultipleSatisfy = useMemo(
    () =>
      isGwpMultipleReady
        ? isCampaignSatisfy(
            cart,
            gwpMultipleId,
            gwpMultipleCampaignType,
            gwpMultiplePurchaseThreshold,
            gwpMultipleQuantityThreshold,
            gwpMultipleGiftLimit,
            totalQuantityOfGiftProductsInCart,
            gwpMultipleTagFilterType,
            gwpMultipleAllowedProductTags,
            gwpMultipleExcludedProductTags,
            '_multiple_gwp',
          )
        : false,
    [cart.state.items],
  );

  const handleGwpRemoveItem = async (product, line) => {
    const payload = [line.id];
    await cart.remove(payload);
    if (line?.properties?._multiple_gwp) {
      setGwpDeletedFromCartCount((prevState) => prevState + 1);
    } else if (line?.properties?._gwp && line?.properties?._gwp_current_id) {
      setGwpAutoAddDeletedIds((prevState) => {
        // Convert ID to string for consistent storage
        const gwpId = String(line?.properties?._gwp_current_id);
        const newIds = [...prevState, gwpId];
        setGwpDismissedIds(newIds); // Persist to session storage
        return newIds;
      });
    }
  };

  useEffect(() => {
    if (isGwpMultipleReady) {
      if (!isGwpMultipleSatisfy) {
        setGwpDeletedFromCartCount(0);
      }

      autoDeleteGwpFromCart(
        gwpMultipleLineItems,
        gwpMultipleId,
        gwpMultipleCampaignType,
        gwpMultiplePurchaseThreshold,
        gwpMultipleQuantityThreshold,
        gwpMultipleGiftLimit,
        totalQuantityOfGiftProductsInCart,
        cart,
        gwpMultipleTagFilterType,
        gwpMultipleAllowedProductTags,
        gwpMultipleExcludedProductTags,
        '_multiple_gwp',
      );
    }
  }, [cart.state.items]);

  let trapFocus = null;

  useEffect(() => {
    if (layout.isBelowBreakpoint('lg')) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      trapFocus = new TrapFocus(document.querySelector('[data-minicart]'));
      document.body.classList.add('no-scroll');
      document.body.classList.add('cart-open');
      trapFocus.init();
    }

    return () => {
      document.body.classList.remove('no-scroll');
      document.body.classList.remove('cart-open');
      if (trapFocus) trapFocus.destroy(document.querySelector('[data-logo]'));
    };
  }, [isOpen, trapFocus]);

  useEffect(() => {
    if (!closeTriggerEl.current) return;
    setPanelTriggers((value) => ({
      ...value,
      closeTriggerEl: closeTriggerEl.current,
    }));
  }, [closeTriggerEl]);

  const handleCheckoutClick = () => {
    if (!lines?.length) {
      window.location = updateURL('/checkout');
    }

    const items = lines?.map((line) => (line.merchandise ? line.merchandise.product.title : line.title))?.join(', ');
    const skus = lines?.map((line) => (line.merchandise ? line.merchandise.product.sku : line.sku))?.join(', ');

    trackMiniCartATB({
      item: items,
      itemId: skus,
      quantity: cart?.state?.item_count,
    });

    // Store the current page path in localStorage before navigating to checkout
    localStorage.setItem('preCheckoutPage', window.location.pathname);

    window.location = updateURL('/checkout');
  };

  const cartCount = useMemo(
    () => cart.state?.items?.reduce((acc, item) => acc + item.quantity, 0) || 0,
    [cart.state.items],
  );

  return (
    <Panel
      triggers={panelTriggers}
      defaultOpen={isCartRedirect}
      showBackdrop={true}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      context="cart"
      additionalClasses="!bg-grey-12 !max-w-[375px] md:!max-w-[540px] text-brown-1"
    >
      <div className="flex flex-col h-full" data-minicart>
        <div
          data-cart-section="header"
          className="flex justify-between bg-white py-4 px-3 md:px-4 text-xs leading-110p tracking-9p font-medium uppercase"
        >
          <div>
            <span className="tracking-[1.08px]">{window.TMW.Strings.cart.title}</span>
            {cartCount > 0 && <span> ({cartCount})</span>}
          </div>
          <button className="pb-1 px-1" type="button" ref={closeTriggerEl} aria-label={window.TMW.Strings.cart.close}>
            <CloseV2 />
          </button>
        </div>

        <div data-cart-section="body" className="bg-transparent h-auto overflow-y-auto grow">
          {timerEnabled && timerId ? (
            <div data-cart-element="timer" className={`gta-block__root ${timerId} bg-white px-3 md:px-4 pb-5`}></div>
          ) : null}

          {upSellProducts?.length > 0 && upSellProducts[0].first_available_variant_id && (
            <div>
              {shUpTitle && <div className="text-headline-tag mb-5">{shUpTitle} </div>}
              <Upsells upsellPromos={upSellProducts} title={shUpTitle} />
            </div>
          )}

          {(promosWithThresholds.length > 0 || freeShippingThreshold > 0) && (
            <FreeShipping
              freeShippingThreshold={freeShippingThreshold}
              freeShippingIconThresholdMet={freeShippingIconThresholdMet}
              freeShippingIconThresholdUnfulfilled={freeShippingIconThresholdUnfulfilled}
              freeShippingMessageThresholdMet={freeShippingMessageThresholdMet}
              freeShippingMessageThresholdUnfulfilled={freeShippingMessageThresholdUnfulfilled}
              cart={cart}
              promosWithThresholds={promosWithThresholds}
              customerMarket={customerMarket}
            />
          )}
          <GwpAutoAddBanner
            cart={cart}
            gwpAutoAddData={gwpAutoAddData}
            gwpDeletedIds={gwpAutoAddDeletedIds}
            setGwpAutoAddDeletedIds={setGwpAutoAddDeletedIds}
            setGwpDismissedIds={setGwpDismissedIds}
            gwpMultipleId={gwpMultipleId}
            gwpMultipleIdx={gwpMultipleIdx}
            isGwpMultipleReady={isGwpMultipleReady}
            gwpMultipleGiftLimit={gwpMultipleGiftLimit}
            gwpMultipleBannerTitle={gwpMultipleBannerTitle}
            gwpMultipleProductTitle={gwpMultipleProductTitle}
            gwpMultiplePromoMessage={gwpMultiplePromoMessage}
            gwpMultipleProductMessage={gwpMultipleProductMessage}
            gwpMultipleCampaignImage={gwpMultipleCampaignImage}
            gwpMultipleProductList={gwpMultipleProductListAvailable}
            gwpMultipleProductTitle1={gwpMultipleProductTitle1}
            gwpMultipleProductTitle2={gwpMultipleProductTitle2}
            gwpMultipleProductTitle3={gwpMultipleProductTitle3}
            gwpMultipleProductSubtitle={gwpMultipleProductSubtitle}
            gwpMultipleProductBadge={gwpMultipleProductBadge}
            isGwpMultipleSatisfy={isGwpMultipleSatisfy}
            totalQuantityOfGiftProductsInCart={totalQuantityOfGiftProductsInCart}
            gwpDeletedFromCartCount={gwpDeletedFromCartCount}
            setGwpDeletedFromCartCount={setGwpDeletedFromCartCount}
          />
          {!!cart.errors?.length && (
            <div className="p-5 pt-8 flex flex-col gap-2">
              {cart.errors.map((error) => (
                <div key={error} className="text-body-2 text-red">
                  {error}
                </div>
              ))}
            </div>
          )}

          {/* empty cart body */}
          {cart?.state?.item_count === 0 && (
            <div data-cart-section="empty cart body" className="mt-8 md:mt-10 px-3 md:px-4">
              <p className="text-[1.125rem] leading-110p tracking-1p">{window.TMW.Strings.cart.empty}</p>
              <p className="text-[1.125rem] leading-110p tracking-1p mb-8">{window.TMW.Strings.cart.emptyTagline}</p>

              {emptyCartButtons.length > 0
                ? emptyCartButtons.map((btn) => (
                    <a
                      key={btn.id}
                      className="button button--primary
                      border-none rounded-100
                      bg-brown-1
                      w-full
                      py-5
                      mb-6
                      text-[.875rem] leading-[26px] tracking-[1.12px] text-grey-12
                      hover:bg-brown-1 hover:opacity-75 hover:text-grey-12"
                      href={btn.url}
                    >
                      {btn.text}
                    </a>
                  ))
                : null}
            </div>
          )}

          <div className="bg-white px-3 md:px-4">
            {lines?.map((line) => {
              const gwpMultipleLineItem = !!line?.properties?._multiple_gwp;
              const gwpMultipleProduct = gwpMultipleLineItem
                ? gwpMultipleProductListAvailable?.find((product) => product.id === line.product_id)
                : undefined;

              const gwpAutoAddLineItem = !!line?.properties?._gwp;
              const gwpAutoAddItem = gwpAutoAddLineItem
                ? gwpAutoAddData?.find((gwpItem) => gwpItem?.product?.id === line.product_id)
                : undefined;
              const gwpAutoAddProduct = gwpAutoAddItem?.product;

              return !!gwpMultipleProduct || !!gwpAutoAddItem?.product ? (
                <GwpMultipleProductItem
                  mode="line-item"
                  key={line.id}
                  line={line}
                  selectedProduct={gwpMultipleProduct || gwpAutoAddProduct}
                  productMessage={gwpAutoAddProduct ? gwpAutoAddItem?.productMessage : gwpMultipleProductMessage}
                  productTitle={gwpAutoAddProduct ? gwpAutoAddItem?.productTitle : gwpMultipleProductTitle}
                  productList={gwpAutoAddProduct ? [gwpAutoAddProduct] : gwpMultipleProductListAvailable}
                  productTitle1={gwpAutoAddProduct ? gwpAutoAddItem.productNewTitle : gwpMultipleProductTitle1}
                  productTitle2={gwpMultipleProductTitle2}
                  productTitle3={gwpMultipleProductTitle3}
                  productSubtitle={gwpAutoAddProduct ? gwpAutoAddItem?.productSubtitle : gwpMultipleProductSubtitle}
                  lineItemPromoMessage1={gwpAutoAddItem?.lineItemPromoMessage1 || ''}
                  productBadge={gwpAutoAddProduct ? gwpAutoAddItem.lineItemPromoMessage2 : gwpMultipleProductBadge}
                  image={gwpAutoAddProduct ? gwpAutoAddItem?.campaignImage : gwpMultipleCampaignImage}
                  handleRemoveItem={handleGwpRemoveItem}
                />
              ) : (
                <CartItem
                  key={line.id}
                  line={line}
                  maxOrderQTY={maxOrderQTY}
                  isMobile={isMobile}
                  promoBadges={promoBadges}
                />
              );
            })}
          </div>

          <div className="mt-[120px] pt-3 empty:hidden" data-nosto-cart-drawer-recommendations>
            {/* this is where Nosto injects the product recommendations widget - use Nosto online editor to modify the widget */}
          </div>

          {/* Nosto tailwind classes that our build tools need to process so that they are available when the Nosto widget is rendered */}
          {/* without these classes the widget won't be styled properly - this must remain hidden */}
          <div className="hidden" aria-hidden="true" role="presentation">
            <p className="w-[140px] md:w-[142px] tracking-[.12px] -top-[1px] rounded-[100px] hover:bg-brown-4"></p>
            <p className="!ml-4 tracking-[.14px] disabled:hover:bg-grey-11 disabled:opacity-50 rounded-[4px]"></p>
            <p className="tracking-[.16px] h-[14px] whitespace-nowrap text-ellipsis opacity-80 py-[9px]"></p>
            <p className="tracking-[.17px]"></p>
            <p className="tracking-[.99px]"></p>
            <p className="tracking-[.72px]"></p>
          </div>
        </div>

        {cart?.state?.item_count > 0 ? (
          <div
            data-cart-section="footer"
            className="bg-white tracking-1p leading-110 mt-4 px-3 md:px-4 pt-5 pb-3 shadow-[0px_-1px_4px_0px_rgba(0,0,0,0.1)]"
          >
            {savingsMessage && totalSavings > 0 && (
              <div
                data-cart-element="savings widget"
                {...(savingsMessage.ab_testing_id ? { id: savingsMessage.ab_testing_id } : {})}
                className="flex justify-between mb-2 text-[1rem] leading-110p tracking-[.16px]"
              >
                <span style={`color: ${savingsMessage.msg_color}`} className="capitalize">
                  {savingsMessage.savings_msg}
                </span>
                <span style={`color: ${savingsMessage.savings_color}`}>
                  -
                  {FormatMoney({
                    amount: totalSavings,
                    currencyCode: window.TMW?.localization?.currencyCode,
                    cartDrawerFooter: true,
                  })}
                </span>
              </div>
            )}

            <div className="flex justify-between text-[1rem] leading-110p tracking-[.16px]">
              <span className="capitalize">{window.TMW.Strings.cart.total}</span>
              <span>
                {FormatMoney({
                  amount: cart?.state?.total_price ? cart.state.total_price / 100 : 0,
                  currencyCode: window.TMW?.localization?.currencyCode,
                  cartDrawerFooter: true,
                })}
              </span>
            </div>

            <button
              className="button button--primary
              border-none rounded-100
              bg-green-1
              w-full
              py-[17px]
              mt-4 mb-2
              text-[.875rem] leading-110p tracking-[1.26px] text-grey-12
              hover:bg-green-2 hover:text-grey-12"
              onClick={handleCheckoutClick}
            >
              {window.TMW.Strings.cart.secureCheckout}
            </button>

            <span className="flex justify-center text-[.75rem] leading-[12px] tracking-[0.25px]">
              {decodeHTMLEntities(window.TMW.Strings.cart.calculatedAtCheckout)}
            </span>
          </div>
        ) : null}

        <button className="sr-only hidden" />
      </div>
    </Panel>
  );
};

export default withCartContext(Cart);
